import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ClipboardList, PackageCheck, ScanLine } from 'lucide-react';
import { ApiError } from '@hutdrop/api-client';
import { formatCents, type StoreOverview } from '@hutdrop/shared';
import { Alert, Button, PageHeader, Skeleton, StockBadge } from '../components/ui';
import { api } from '../lib/api';
import { useStaffAuth } from '../lib/auth';
import { HoldLeft, OrderStatusBadge } from './OrdersPage';

const n = new Intl.NumberFormat();

function Kpi({ label, value, to, tone }: { label: string; value: string | number; to?: string; tone?: 'warn' | 'good' }) {
  const body = (
    <>
      <span className="kpi__label">{label}</span>
      <strong className={`kpi__value sd-num${tone ? ` kpi__value--${tone}` : ''}`}>{value}</strong>
    </>
  );
  return to ? (
    <Link to={to} className="kpi kpi--link">
      {body}
    </Link>
  ) : (
    <div className="kpi">{body}</div>
  );
}

export function OverviewPage() {
  const { can, staff } = useStaffAuth();
  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.request<StoreOverview>('/v1/admin/overview', { auth: true }),
    refetchInterval: 30_000,
  });
  if (overview.isPending) return <Skeleton rows={10} />;
  if (overview.isError) return <Alert>{overview.error.message}</Alert>;
  const o = overview.data;

  return (
    <div className="page page--wide">
      <PageHeader
        title={`Hi, ${staff!.user.firstName}`}
        subtitle={`Today in store time (${o.timezone}). Pre-orders count as sales only once picked up.`}
        actions={
          can('orders.scan') ? (
            <Link to="/orders/scan" className="btn btn--primary">
              <ScanLine size={16} aria-hidden="true" /> Scan pickup code
            </Link>
          ) : undefined
        }
      />

      <h2 className="section-h">Orders</h2>
      <div className="kpis">
        <Kpi label="New" value={o.orders.new} to="/orders?queue=new" tone={o.orders.new ? 'warn' : undefined} />
        <Kpi label="Being gathered" value={o.orders.gathering} to="/orders?queue=in_progress" />
        <Kpi label="Ready for pickup" value={o.orders.ready} to="/orders?queue=ready" />
        <Kpi label="Expiring within 2 h" value={o.orders.expiringSoon} to="/orders?queue=expiring" tone={o.orders.expiringSoon ? 'warn' : undefined} />
        <Kpi label="Open pre-order value" value={formatCents(o.orders.openValueCents)} />
        <Kpi label="Placed today" value={o.orders.placedToday} />
        <Kpi label="Picked up today" value={o.orders.pickedUpToday} tone="good" />
        <Kpi label="Sales today (picked up, est.)" value={formatCents(o.orders.salesTodayCents)} tone="good" />
        <Kpi label="Expired today" value={o.orders.expiredToday} />
      </div>

      <h2 className="section-h">Inventory & deals</h2>
      <div className="kpis">
        <Kpi label="Active products" value={n.format(o.inventory.activeProducts)} to="/products" />
        <Kpi label="Low stock" value={o.inventory.lowStock} to="/products?stock=LOW_STOCK" tone={o.inventory.lowStock ? 'warn' : undefined} />
        <Kpi label="Out of stock" value={o.inventory.outOfStock} to="/products?stock=OUT_OF_STOCK" tone={o.inventory.outOfStock ? 'warn' : undefined} />
        <Kpi label="Units on hand" value={n.format(o.inventory.unitsOnHand)} />
        <Kpi label="Held by pre-orders" value={n.format(o.inventory.unitsReserved)} />
        <Kpi label="Retail value on hand" value={formatCents(o.inventory.retailValueCents)} />
        <Kpi label="Deals running" value={o.deals.active} to={can('promotions.manage') ? '/promotions' : undefined} />
        <Kpi label="Flash deals live" value={o.deals.flashLive} />
      </div>

      <div className="two-col overview-cols">
        <section className="card">
          <h2 className="card__title">
            <AlertTriangle size={16} aria-hidden="true" /> Low & out of stock
          </h2>
          {o.lowStock.length === 0 ? (
            <p className="muted">Everything active is above its low-stock threshold.</p>
          ) : (
            <ul className="restock-list">
              {o.lowStock.map((p) => (
                <RestockRow key={p.id} p={p} canReceive={can('inventory.receive')} />
              ))}
            </ul>
          )}
        </section>

        <section className="card">
          <h2 className="card__title">
            <ClipboardList size={16} aria-hidden="true" /> Latest pre-orders
          </h2>
          {o.recentOrders.length === 0 ? (
            <p className="muted">No pre-orders yet.</p>
          ) : (
            <ul className="list">
              {o.recentOrders.map((r) => (
                <li key={r.id} className="list__row">
                  <span className="list__main">
                    <Link to={`/orders/${r.id}`} className="order-link sd-num">
                      {r.orderNumber}
                    </Link>{' '}
                    <span className="muted">
                      {r.customerName} · {r.itemCount} items · {formatCents(r.estimatedTotalCents)}
                    </span>
                  </span>
                  <span className="list__tools">
                    {['CREATED', 'PENDING', 'READY_FOR_PICKUP'].includes(r.status) ? <HoldLeft expiresAt={r.expiresAt} /> : null}
                    <OrderStatusBadge status={r.status} label={r.statusLabel} />
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/orders?queue=active" className="link-btn">
            <PackageCheck size={14} aria-hidden="true" /> All open orders
          </Link>
        </section>
      </div>
    </div>
  );
}

/** One product with a quick "received N" action (a normal receiving entry in the ledger). */
function RestockRow({ p, canReceive }: { p: StoreOverview['lowStock'][number]; canReceive: boolean }) {
  const qc = useQueryClient();
  const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const amount = Number.parseInt(qty, 10);
  async function receive() {
    setBusy(true);
    setMsg(null);
    try {
      await api.request('/v1/admin/inventory/receiving', { method: 'POST', auth: true, body: { note: 'Quick restock from dashboard', lines: [{ productId: p.id, quantity: amount }] } });
      setQty('');
      await qc.invalidateQueries({ queryKey: ['overview'] });
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <li className="restock-row">
      <span className="restock-row__main">
        <Link to={`/products/${p.id}`} className="order-link">
          {p.name}
        </Link>
        <span className="muted small">
          {p.available} available{p.reserved ? ` (${p.reserved} held)` : ''} · alert at {p.threshold}
          {p.sku ? ` · ${p.sku}` : ''}
        </span>
        {msg ? <span className="fld__error">{msg}</span> : null}
      </span>
      <StockBadge status={p.status} />
      {canReceive ? (
        <form
          className="restock-row__form"
          onSubmit={(e) => {
            e.preventDefault();
            if (amount > 0) void receive();
          }}
        >
          <input className="fld__input" type="number" min={1} max={100000} inputMode="numeric" placeholder="Qty" aria-label={`Units received for ${p.name}`} value={qty} onChange={(e) => setQty(e.target.value)} />
          <Button type="submit" variant="secondary" busy={busy} disabled={!(amount > 0)}>
            Receive
          </Button>
        </form>
      ) : null}
    </li>
  );
}
